import { gsap, ScrollTrigger, motion, withLoader, endIntro, reveals, refreshAfterFonts } from "./common.js";

const root = document.querySelector(".qb-plat");
const q = (s) => root.querySelector(s);
const qa = (s) => gsap.utils.toArray(root.querySelectorAll(s));

const pills = qa(".pl-pill");
const dots = qa(".hs__dots i");
const panels = qa(".panel");
let active = -1;
let gallery = null;

// 0 = Architecture, 1–5 = the five parts, 6 = Specs.
function setActive(i) {
  if (i === active) return;
  active = i;
  pills.forEach((p, n) => {
    p.classList.toggle("on", n === i);
    if (n === i) p.setAttribute("aria-current", "true");
    else p.removeAttribute("aria-current");
  });
  dots.forEach((d, n) => d.classList.toggle("on", n === i - 1));
}

const maxX = () => Math.max(0, q(".hs__track").scrollWidth - window.innerWidth);

// With the sideways gallery, a part's anchor is a scroll position, not an element.
pills.forEach((p) =>
  p.addEventListener("click", (e) => {
    if (!motion) return;
    const panel = p.getAttribute("data-panel-go");
    if (panel !== null && gallery) {
      e.preventDefault();
      const st = gallery.scrollTrigger;
      const max = maxX();
      const x = Math.min(max, panels[+panel].offsetLeft - panels[0].offsetLeft);
      window.scrollTo({ top: st.start + (max ? x / max : 0) * (st.end - st.start) + 2, behavior: "smooth" });
      return;
    }
    const id = p.getAttribute("data-go");
    const el = id && document.getElementById(id);
    if (el) {
      e.preventDefault();
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 120, behavior: "smooth" });
    }
  }),
);

if (motion) {
  scenes();
  if (withLoader) intro();
  else heroIn();
  refreshAfterFonts();
}

function intro() {
  const letters = qa(".pl-loader__t span");
  gsap.set(".pl-line > span", { yPercent: 120 });
  gsap.set(".pl-in", { autoAlpha: 0, y: 24 });
  gsap.set(".pl-sub", { autoAlpha: 0 });
  gsap
    .timeline({ onComplete: endIntro })
    .fromTo(letters, { yPercent: 110 }, { yPercent: 0, duration: 0.9, ease: "expo.out", stagger: 0.04 }, 0.05)
    .fromTo(".pl-loader__bar i", { scaleX: 0 }, { scaleX: 1, duration: 1.1, ease: "power2.inOut" }, 0)
    .to(letters, { yPercent: -110, duration: 0.6, ease: "power3.in", stagger: 0.025 }, 1.05)
    .to(".pl-loader", { clipPath: "inset(0% 0% 100% 0%)", duration: 0.95, ease: "expo.inOut" }, 1.3)
    .to(".pl-line > span", { yPercent: 0, duration: 1.3, ease: "expo.out", stagger: 0.09 }, 1.75)
    .to(".pl-in", { autoAlpha: 1, y: 0, duration: 1, ease: "expo.out", stagger: 0.1 }, 1.9)
    .to(".pl-sub", { autoAlpha: 1, duration: 0.8 }, 2.2);
}

function heroIn() {
  gsap
    .timeline({ defaults: { ease: "expo.out" } })
    .from(".pl-line > span", { yPercent: 120, duration: 1.2, stagger: 0.08 })
    .from(".pl-in", { autoAlpha: 0, y: 24, duration: 1, stagger: 0.1 }, 0.15);
}

function scenes() {
  reveals(root);

  // Architecture: a request travels in, then fans out to each part.
  const routes = qa(".route");
  const at = gsap.timeline({
    scrollTrigger: {
      trigger: ".arch", start: "top 78%", end: "bottom 55%", scrub: 1,
      onToggle: (self) => self.isActive && setActive(0),
    },
  });
  at.from(qa(".arch [data-n]"), { autoAlpha: 0, y: 30, stagger: 0.15, duration: 0.6, ease: "power2.out" })
    .from(".w-in i", { scaleX: 0, duration: 0.6, ease: "none" }, 0.3)
    .fromTo(".w-in em", { left: "0%", opacity: 0 }, { left: "100%", opacity: 1, duration: 0.6, ease: "power1.in" }, 0.3)
    .to(".w-in em", { opacity: 0, duration: 0.1 }, 0.9)
    .from(".node--gw", { boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.14), 0 0 0px 0px rgba(76,201,217,0)", duration: 0.4 }, 0.85)
    .from(".fan__v i", { scaleY: 0, duration: 0.5, ease: "none" }, 1.0)
    .from(qa(".fan__h i"), { scaleX: 0, duration: 0.4, stagger: 0.08, ease: "none" }, 1.25)
    .from(routes, { autoAlpha: 0.25, x: 20, duration: 0.4, stagger: 0.08 }, 1.3);
  routes.forEach((r, i) => at.call(() => r.classList.toggle("lit", at.scrollTrigger.direction > 0), null, 1.55 + i * 0.08));

  // The five parts, pinned and scrolled sideways. Each panel loads as it arrives.
  const hs = q(".hs");
  const sizeGallery = () => { hs.style.height = `${maxX() + window.innerHeight}px`; };
  sizeGallery();
  ScrollTrigger.addEventListener("refreshInit", sizeGallery);
  gallery = gsap.to(".hs__track", {
    x: () => -maxX(),
    ease: "none",
    scrollTrigger: {
      trigger: hs, start: "top top", end: () => `+=${maxX()}`, scrub: 0.8, invalidateOnRefresh: true,
      onUpdate: (self) => setActive(1 + Math.round(self.progress * (panels.length - 1))),
      onLeave: () => setActive(6),
      onLeaveBack: () => setActive(0),
    },
  });
  panels.forEach((p, i) => {
    gsap.set(p.querySelectorAll(".panel__copy > *, .pv > *"), { autoAlpha: 0, y: 18 });
    const when = i === 0 ? { trigger: hs, start: "top 55%" } : { trigger: p, containerAnimation: gallery, start: "left 75%" };
    ScrollTrigger.create({
      ...when,
      once: true,
      onEnter: () =>
        gsap.delayedCall(0.35, () => {
          p.classList.add("is-in");
          gsap.to(p.querySelectorAll(".panel__copy > *"), { autoAlpha: 1, y: 0, duration: 0.9, ease: "expo.out", stagger: 0.07 });
          gsap.to(p.querySelectorAll(".pv > *"), { autoAlpha: 1, y: 0, duration: 0.9, ease: "expo.out", stagger: 0.08, delay: 0.25 });
        }),
    });
    if (i > 0) {
      gsap.fromTo(p, { scale: 0.92, opacity: 0.55 }, {
        scale: 1, opacity: 1, ease: "none",
        scrollTrigger: { trigger: p, containerAnimation: gallery, start: "left 100%", end: "left 45%", scrub: true },
      });
    }
  });

  // Specs: each rule draws across, then its row rises.
  qa(".spec").forEach((s) => {
    gsap.fromTo(s, { "--ln": 0 }, { "--ln": 1, duration: 1.2, ease: "expo.out", scrollTrigger: { trigger: s, start: "top 92%" } });
    gsap.from(s.children, { autoAlpha: 0, y: 18, duration: 0.9, ease: "expo.out", stagger: 0.08, scrollTrigger: { trigger: s, start: "top 92%" } });
  });
  ScrollTrigger.create({ trigger: "#specs", start: "top 60%", end: "bottom 40%", onToggle: (self) => self.isActive && setActive(6) });
}
